import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FadeUp, Stagger, staggerItem } from "../components/Reveal";
import PageHeader from "../components/PageHeader";
import { CAPABILITIES } from "../data/capabilities";

function CapabilitySection({ block, index }) {
  const flipped = index % 2 === 1;
  return (
    <section
      id={block.id}
      className="py-24 md:py-32 border-t border-[var(--line)]"
      data-testid={`capability-${block.id}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 md:mb-20">
          <div className="md:col-span-2">
            <p className="font-mono text-xs text-[var(--gold-soft)] tracking-[0.24em]">{block.number}</p>
          </div>
          <div className="md:col-span-10">
            <FadeUp>
              <h2 className="h-section text-[clamp(2.4rem,6vw,5rem)] text-[var(--navy)]">
                {block.title}
              </h2>
              <p className="mt-8 max-w-2xl text-[var(--charcoal)] text-lg leading-relaxed">{block.lead}</p>
            </FadeUp>
          </div>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-12 gap-10 items-start ${flipped ? "md:[&>*:first-child]:order-2" : ""}`}>
          <FadeUp delay={0.1} className="md:col-span-5">
            <div className="relative overflow-hidden aspect-[4/5] group">
              <img
                src={block.image}
                alt={block.title}
                className="w-full h-full object-cover grayscale-[10%] transition-transform duration-[1500ms] ease-out group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/50 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <p className="eyebrow-gold text-[10px]">Field Program · {block.number}</p>
                <p className="font-serif text-2xl mt-2">{block.title}</p>
              </div>
            </div>
          </FadeUp>

          <div className="md:col-span-7">
            <div className="border-t border-[var(--line)]">
              {block.items.map((it, i) => (
                <FadeUp key={it.name} delay={i * 0.04}>
                  <div className="grid grid-cols-12 gap-4 py-6 border-b border-[var(--line)] group hover:bg-[var(--paper)] transition-colors duration-300">
                    <div className="col-span-1 font-mono text-xs text-[var(--gold-soft)] pt-1">{String(i + 1).padStart(2, "0")}</div>
                    <div className="col-span-11 md:col-span-4">
                      <p className="font-serif text-xl md:text-2xl text-[var(--navy)]">{it.name}</p>
                    </div>
                    <div className="col-span-12 md:col-span-7 text-[var(--steel)] leading-relaxed">{it.desc}</div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StickyIndex() {
  const [active, setActive] = useState(CAPABILITIES[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    CAPABILITIES.forEach((b) => {
      const el = document.getElementById(b.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <aside className="hidden lg:block sticky top-24">
      <p className="eyebrow-gold mb-6">Index</p>
      <ul className="space-y-4">
        {CAPABILITIES.map((b) => (
          <li key={b.id}>
            <a
              href={`#${b.id}`}
              data-testid={`capability-index-${b.id}`}
              className={`group flex items-start gap-3 text-sm transition-colors duration-300 ${
                active === b.id ? "text-[var(--navy)]" : "text-[var(--steel)] hover:text-[var(--navy)]"
              }`}
            >
              <span className={`font-mono text-[10px] pt-1 ${active === b.id ? "text-[var(--gold-soft)]" : "text-[var(--muted-foreground)]"}`}>
                {b.number}
              </span>
              <span className="font-serif text-xl leading-tight">
                {b.title}
              </span>
              {active === b.id && (
                <motion.span layoutId="cap-dot" className="mt-2 inline-block w-1.5 h-1.5 bg-[var(--gold)]" />
              )}
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
}

export default function Capabilities() {
  return (
    <div>
      <PageHeader
        eyebrow="§ Capabilities"
        title={["Five disciplines,", "one engineering", "practice."]}
        description="Nuclear technology, space research, strategic minerals, applied research, and sustainability — the five practices that comprise Aurelius Dynamics."
        meta={<span>Chapter II / IV</span>}
      />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <StickyIndex />
          </div>
          <div className="lg:col-span-9">
            {CAPABILITIES.map((block, i) => (
              <CapabilitySection key={block.id} block={block} index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
